import { useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { useNativeLocation } from "@/hooks/use-native-location";
import { useDashboard } from "@/contexts/dashboard-context";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";

/**
 * Shared hook for tracking actions that can be called from UI or Siri intents.
 * Returns functions to start/stop commute and block tracking.
 */
export function useTrackingActions() {
  const { activeShift, activeBlock } = useDashboard();

  const startShiftMutation = useMutation(api.shifts.startShift);
  const endShiftMutation = useMutation(api.shifts.endShift);
  const pauseShiftMutation = useMutation(api.shifts.pauseShift);
  const resumeShiftMutation = useMutation(api.shifts.resumeShift);
  const batchSyncLocationPoints = useMutation(api.shifts.batchSyncLocationPoints);

  const {
    isReady,
    isTracking,
    startTracking,
    stopTracking,
    getCurrentPosition,
    getLocationPoints,
    clearSession,
  } = useNativeLocation();

  const isShiftActive = activeShift && activeShift.status !== "completed";

  /**
   * Start commute tracking
   */
  const startCommute = useCallback(async (): Promise<boolean> => {
    if (!isReady) {
      toast({
        title: "Location not ready",
        description: "Please wait for location services to initialize",
        variant: "destructive",
      });
      return false;
    }

    if (isShiftActive) {
      toast({
        title: "Already tracking",
        description: "Stop your current tracking session first",
        variant: "destructive",
      });
      return false;
    }

    try {
      const position = await getCurrentPosition();
      if (!position) {
        throw new Error("Could not get current position");
      }

      const shiftId = await startShiftMutation({
        lat: position.lat,
        lng: position.lng,
        type: "commute",
      });

      const trackingStarted = await startTracking({
        shiftId: shiftId,
        initialLat: position.lat,
        initialLng: position.lng,
      });

      if (!trackingStarted) {
        await endShiftMutation({ shiftId });
        toast({
          title: "Location permission required",
          description: 'Enable "Always" location access in Settings to track mileage.',
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Commute started",
        description: "Tracking your mileage",
      });
      return true;
    } catch (error) {
      toast({
        title: "Failed to start commute",
        description: getErrorMessage(error),
        variant: "destructive",
      });
      return false;
    }
  }, [isReady, isShiftActive, getCurrentPosition, startShiftMutation, startTracking, endShiftMutation]);

  /**
   * Start block tracking (requires activeBlock)
   */
  const startBlock = useCallback(async (): Promise<boolean> => {
    if (!isReady) {
      toast({
        title: "Location not ready",
        description: "Please wait for location services to initialize",
        variant: "destructive",
      });
      return false;
    }

    if (!activeBlock) {
      toast({
        title: "No active block",
        description: "Add a block first before starting route tracking",
        variant: "destructive",
      });
      return false;
    }

    // Check if there's an active commute - stop it first
    const isCommuteActive = isShiftActive && !activeShift?.blockId && activeShift?.type !== "block";

    try {
      if (isCommuteActive && activeShift) {
        // Stop the commute first
        const nativePoints = await getLocationPoints(activeShift._id);
        const trackingData = await stopTracking();
        const milesToSave = trackingData?.totalMiles ?? 0;

        if (nativePoints.length > 0) {
          await batchSyncLocationPoints({
            shiftId: activeShift._id,
            points: nativePoints,
          });
        }

        await endShiftMutation({
          shiftId: activeShift._id,
          totalMiles: milesToSave,
        });

        await clearSession(activeShift._id);

        toast({
          title: "Commute saved",
          description: `${milesToSave.toFixed(1)} mi tracked`,
        });
      } else if (isShiftActive) {
        toast({
          title: "Already tracking a block",
          description: "Stop your current tracking session first",
          variant: "destructive",
        });
        return false;
      }

      // Start block tracking
      const position = await getCurrentPosition();
      if (!position) {
        throw new Error("Could not get current position");
      }

      const shiftId = await startShiftMutation({
        lat: position.lat,
        lng: position.lng,
        blockId: activeBlock._id,
      });

      const trackingStarted = await startTracking({
        shiftId: shiftId,
        initialLat: position.lat,
        initialLng: position.lng,
      });

      if (!trackingStarted) {
        await endShiftMutation({ shiftId });
        toast({
          title: "Location permission required",
          description: 'Enable "Always" location access in Settings to track mileage.',
          variant: "destructive",
        });
        return false;
      }

      toast({
        title: "Route tracking started",
        description: "Tracking your delivery mileage",
      });
      return true;
    } catch (error) {
      toast({
        title: "Failed to start route",
        description: getErrorMessage(error),
        variant: "destructive",
      });
      return false;
    }
  }, [
    isReady,
    activeBlock,
    activeShift,
    isShiftActive,
    getCurrentPosition,
    startShiftMutation,
    startTracking,
    endShiftMutation,
    getLocationPoints,
    stopTracking,
    batchSyncLocationPoints,
    clearSession,
  ]);

  /**
   * Stop current tracking (commute or block)
   */
  const stopCurrentTracking = useCallback(async (): Promise<boolean> => {
    if (!activeShift || activeShift.status === "completed") {
      toast({
        title: "Nothing to stop",
        description: "No active tracking session",
        variant: "destructive",
      });
      return false;
    }

    try {
      const nativePoints = await getLocationPoints(activeShift._id);
      const trackingData = await stopTracking();
      const milesToSave = trackingData?.totalMiles ?? 0;

      if (nativePoints.length > 0) {
        await batchSyncLocationPoints({
          shiftId: activeShift._id,
          points: nativePoints,
        });
      }

      await endShiftMutation({
        shiftId: activeShift._id,
        totalMiles: milesToSave,
      });

      await clearSession(activeShift._id);

      const duration = Math.round(
        (Date.now() - activeShift.startedAt - (activeShift.pauseDuration || 0)) / 60000
      );

      const trackingType = activeShift.blockId || activeShift.type === "block" ? "Route" : "Commute";

      toast({
        title: `${trackingType} saved`,
        description: `${milesToSave.toFixed(1)} mi, ${duration} min`,
      });

      return true;
    } catch (error) {
      toast({
        title: "Failed to stop tracking",
        description: getErrorMessage(error),
        variant: "destructive",
      });
      return false;
    }
  }, [
    activeShift,
    getLocationPoints,
    stopTracking,
    batchSyncLocationPoints,
    endShiftMutation,
    clearSession,
  ]);

  /**
   * Pause current tracking
   */
  const pauseCurrentTracking = useCallback(async (): Promise<boolean> => {
    console.log("[TrackingActions] pauseCurrentTracking called, activeShift:", activeShift ? { id: activeShift._id, status: activeShift.status, type: activeShift.type } : null);

    if (!activeShift || activeShift.status !== "active") {
      console.log("[TrackingActions] Nothing to pause - activeShift:", activeShift?.status);
      toast({
        title: "Nothing to pause",
        description: "No active tracking session",
        variant: "destructive",
      });
      return false;
    }

    try {
      await pauseShiftMutation({ shiftId: activeShift._id });

      const trackingType = activeShift.blockId || activeShift.type === "block" ? "Route" : "Commute";

      toast({
        title: `${trackingType} paused`,
        description: "Tracking is paused. Say 'Resume' to continue.",
      });

      return true;
    } catch (error) {
      toast({
        title: "Failed to pause",
        description: getErrorMessage(error),
        variant: "destructive",
      });
      return false;
    }
  }, [activeShift, pauseShiftMutation]);

  /**
   * Resume paused tracking
   */
  const resumeCurrentTracking = useCallback(async (): Promise<boolean> => {
    if (!activeShift || activeShift.status !== "paused") {
      toast({
        title: "Nothing to resume",
        description: "No paused tracking session",
        variant: "destructive",
      });
      return false;
    }

    try {
      await resumeShiftMutation({ shiftId: activeShift._id });

      const trackingType = activeShift.blockId || activeShift.type === "block" ? "Route" : "Commute";

      toast({
        title: `${trackingType} resumed`,
        description: "Tracking has resumed",
      });

      return true;
    } catch (error) {
      toast({
        title: "Failed to resume",
        description: getErrorMessage(error),
        variant: "destructive",
      });
      return false;
    }
  }, [activeShift, resumeShiftMutation]);

  return {
    startCommute,
    startBlock,
    stopCurrentTracking,
    pauseCurrentTracking,
    resumeCurrentTracking,
    isReady,
    isTracking,
    isShiftActive,
    isPaused: activeShift?.status === "paused",
    hasActiveBlock: !!activeBlock,
  };
}
